/**
 * Leviathan Web - Dispatch Page
 * Quasar Prime LLC
 * Operational Command Center - Dispatch Board with Drag-Drop
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Truck,
  User,
  MapPin,
  Clock,
  Package,
  CheckCircle,
  XCircle,
  AlertTriangle,
  GripVertical,
  Filter,
  Eye,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { API_URL } from '@/config/api';

interface DispatchItem {
  id: string;
  dispatchNumber: string;
  status: string;
  scheduledDate: string;
  scheduledTime: string | null;
  estimatedDuration: number | null;
  departedAt: string | null;
  arrivedAt: string | null;
  completedAt: string | null;
  order: {
    id: string;
    orderNumber: string;
    total: number;
    deliveryAddress: string | null;
    deliveryCity: string | null;
    deliveryState: string | null;
    customer: {
      id: string;
      name: string;
      customerNumber: string;
    };
    items: Array<{
      id: string;
      quantity: number;
      product: {
        name: string;
        sku: string;
      };
    }>;
  };
  truck: {
    id: string;
    unitNumber: string;
    name: string | null;
    totalCapacity: number | null;
  };
  driver: {
    id: string;
    driverNumber: string;
    firstName: string;
    lastName: string;
    phone: string | null;
  };
}

interface DispatchBoard {
  SCHEDULED: DispatchItem[];
  EN_ROUTE: DispatchItem[];
  ARRIVED: DispatchItem[];
  DELIVERING: DispatchItem[];
  COMPLETED: DispatchItem[];
  CANCELLED: DispatchItem[];
  FAILED: DispatchItem[];
}

interface DispatchStats {
  todayScheduled: number;
  activeDispatches: number;
  completedToday: number;
  byStatus: {
    scheduled: number;
    enRoute: number;
    arrived: number;
    delivering: number;
    completed: number;
    cancelled: number;
    failed: number;
  };
}

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; icon: React.ElementType }
> = {
  SCHEDULED: {
    label: 'Scheduled',
    color: 'text-steel',
    bgColor: 'bg-steel/20 border-steel/50',
    icon: Clock,
  },
  EN_ROUTE: {
    label: 'En Route',
    color: 'text-amber',
    bgColor: 'bg-amber/20 border-amber/50',
    icon: Truck,
  },
  ARRIVED: {
    label: 'Arrived',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-400/20 border-cyan-400/50',
    icon: MapPin,
  },
  DELIVERING: {
    label: 'Delivering',
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/20 border-purple-400/50',
    icon: Package,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'text-success-green',
    bgColor: 'bg-success-green/20 border-success-green/50',
    icon: CheckCircle,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-text-tertiary',
    bgColor: 'bg-panel-border/20 border-panel-border/50',
    icon: XCircle,
  },
  FAILED: {
    label: 'Failed',
    color: 'text-error-red',
    bgColor: 'bg-error-red/20 border-error-red/50',
    icon: AlertTriangle,
  },
};

const DispatchPage = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState<DispatchBoard | null>(null);
  const [stats, setStats] = useState<DispatchStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [activeDispatch, setActiveDispatch] = useState<DispatchItem | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // All dispatches table state
  const [allDispatches, setAllDispatches] = useState<DispatchItem[]>([]);
  const [allDispatchesTotal, setAllDispatchesTotal] = useState(0);
  const [allDispatchesPage, setAllDispatchesPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    })
  );

  const fetchBoard = async () => {
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await fetch(`${API_URL}/api/dispatch/board?date=${dateStr}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBoard(data.board);
      }
    } catch (error) {
      console.error('Error fetching dispatch board:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dispatch/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching dispatch stats:', error);
    }
  };

  const fetchAllDispatches = async () => {
    setIsLoadingAll(true);
    try {
      const params = new URLSearchParams({
        page: String(allDispatchesPage),
        limit: '25',
      });
      if (statusFilter) params.set('status', statusFilter);
      if (dateFilter) params.set('scheduledDate', dateFilter);
      if (searchFilter) params.set('search', searchFilter);

      const response = await fetch(`${API_URL}/api/dispatch?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAllDispatches(data.data);
        setAllDispatchesTotal(data.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching all dispatches:', error);
    } finally {
      setIsLoadingAll(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchBoard(), fetchStats(), fetchAllDispatches()]);
      setIsLoading(false);
    };
    loadData();
  }, [selectedDate, accessToken]);

  useEffect(() => {
    fetchAllDispatches();
  }, [statusFilter, dateFilter, searchFilter, allDispatchesPage]);

  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Active statuses to show in columns
  const activeStatuses = ['SCHEDULED', 'EN_ROUTE', 'ARRIVED', 'DELIVERING', 'COMPLETED'];

  // Find a dispatch by ID across all board columns
  const findDispatchById = (id: string): DispatchItem | null => {
    if (!board) return null;
    for (const status of Object.keys(board)) {
      const found = board[status as keyof DispatchBoard].find(d => d.id === id);
      if (found) return found;
    }
    return null;
  };

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const dispatch = findDispatchById(event.active.id as string);
    setActiveDispatch(dispatch);
  };

  // Handle drag end - update status via API
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDispatch(null);

    if (!over || !board) return;

    const dispatchId = active.id as string;
    const newStatus = over.id as string;

    // Find the dispatch and its current status
    let currentStatus: string | null = null;
    for (const status of Object.keys(board)) {
      if (board[status as keyof DispatchBoard].find(d => d.id === dispatchId)) {
        currentStatus = status;
        break;
      }
    }

    if (!currentStatus || currentStatus === newStatus) return;

    // COMPLETED requires delivery data - redirect to detail page instead of drag
    if (newStatus === 'COMPLETED') {
      navigate(`/dispatch/${dispatchId}`);
      return;
    }

    // Optimistic update
    const dispatch = findDispatchById(dispatchId);
    if (!dispatch) return;

    setBoard(prev => {
      if (!prev) return prev;
      const newBoard = { ...prev };
      // Remove from old column
      newBoard[currentStatus as keyof DispatchBoard] = newBoard[currentStatus as keyof DispatchBoard].filter(
        d => d.id !== dispatchId
      );
      // Add to new column
      newBoard[newStatus as keyof DispatchBoard] = [
        ...newBoard[newStatus as keyof DispatchBoard],
        { ...dispatch, status: newStatus },
      ];
      return newBoard;
    });

    // API call
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/dispatch/${dispatchId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        // Rollback on error
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update dispatch status:', errorData.error || 'Unknown error');
        await fetchBoard();
      } else {
        // Refresh stats and all dispatches table
        await Promise.all([fetchStats(), fetchAllDispatches()]);
      }
    } catch (error) {
      // Rollback on error
      await fetchBoard();
      console.error('Error updating dispatch status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Dispatch Command
              </h1>
              <p className="text-text-secondary text-sm mt-1 font-mono">
                Operational Control Center
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  fetchBoard();
                  fetchStats();
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border hover:border-steel transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">Refresh</span>
              </button>
              <Link
                to="/dispatch/new"
                className="flex items-center space-x-2 px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all"
                style={{ boxShadow: '0 0 15px rgba(255, 149, 0, 0.4)' }}
              >
                <Plus className="w-5 h-5" />
                <span>New Dispatch</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Today's Schedule"
          value={stats?.todayScheduled ?? 0}
          color="steel"
          icon={Calendar}
        />
        <StatCard
          title="Active Deliveries"
          value={stats?.activeDispatches ?? 0}
          color="amber"
          icon={Truck}
        />
        <StatCard
          title="Completed Today"
          value={stats?.completedToday ?? 0}
          color="success-green"
          icon={CheckCircle}
        />
        <StatCard
          title="Failed/Cancelled"
          value={(stats?.byStatus.failed ?? 0) + (stats?.byStatus.cancelled ?? 0)}
          color="error-red"
          icon={AlertTriangle}
        />
      </div>

      {/* Date Navigation */}
      <div className="flex items-center justify-between bg-panel-dark border border-panel-border p-4">
        <button
          onClick={handlePrevDay}
          className="flex items-center space-x-2 px-4 py-2 bg-background hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border hover:border-steel transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm font-mono">Previous</span>
        </button>

        <div className="flex items-center space-x-4">
          <Calendar className="w-6 h-6 text-amber" />
          <span className="text-xl font-display text-text-primary uppercase tracking-wide">
            {formatDate(selectedDate)}
          </span>
          {!isToday(selectedDate) && (
            <button
              onClick={handleToday}
              className="px-3 py-1 text-xs font-mono uppercase text-amber border border-amber hover:bg-amber/20 transition-all"
            >
              Today
            </button>
          )}
          {isToday(selectedDate) && (
            <span className="px-3 py-1 text-xs font-mono uppercase text-success-green bg-success-green/20 border border-success-green/50">
              Today
            </span>
          )}
        </div>

        <button
          onClick={handleNextDay}
          className="flex items-center space-x-2 px-4 py-2 bg-background hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border hover:border-steel transition-all"
        >
          <span className="text-sm font-mono">Next</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Dispatch Board */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center space-x-4">
            <div className="w-3 h-3 bg-amber animate-pulse" />
            <span className="text-text-secondary font-mono">Loading dispatch board...</span>
          </div>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-5 gap-4">
            {activeStatuses.map((status) => {
              const config = STATUS_CONFIG[status]!;
              const dispatches = board?.[status as keyof DispatchBoard] ?? [];

              return (
                <DroppableColumn
                  key={status}
                  status={status}
                  config={config}
                  dispatches={dispatches}
                  isUpdating={isUpdating}
                />
              );
            })}
          </div>

          {/* Drag Overlay - shows the card being dragged */}
          <DragOverlay>
            {activeDispatch ? (
              <div className="opacity-90">
                <DispatchCard dispatch={activeDispatch} isDragging />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Failed/Cancelled Section */}
      {board && (board.FAILED.length > 0 || board.CANCELLED.length > 0) && (
        <div className="bg-panel-dark border border-error-red/30 p-4">
          <h3 className="text-lg font-display text-error-red uppercase tracking-wide mb-4">
            Failed & Cancelled Dispatches
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...board.FAILED, ...board.CANCELLED].map((dispatch) => (
              <DispatchCard key={dispatch.id} dispatch={dispatch} compact />
            ))}
          </div>
        </div>
      )}

      {/* All Active Dispatches Table */}
      <div className="bg-panel-dark border border-panel-border">
        <div className="flex items-center justify-between p-4 border-b border-panel-border">
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-amber" />
            <h2 className="text-xl font-display text-text-primary uppercase tracking-wide">
              All Dispatches
            </h2>
            <span className="text-text-tertiary font-mono text-sm">
              ({allDispatchesTotal} total)
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => { setSearchFilter(e.target.value); setAllDispatchesPage(1); }}
              placeholder="Search..."
              className="bg-background border border-panel-border focus:border-amber px-3 py-1.5 text-text-primary font-mono text-sm w-40"
            />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setAllDispatchesPage(1); }}
              className="bg-background border border-panel-border focus:border-amber px-3 py-1.5 text-text-primary font-mono text-sm"
            >
              <option value="">All Statuses</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="EN_ROUTE">En Route</option>
              <option value="ARRIVED">Arrived</option>
              <option value="DELIVERING">Delivering</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="FAILED">Failed</option>
            </select>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => { setDateFilter(e.target.value); setAllDispatchesPage(1); }}
              className="bg-background border border-panel-border focus:border-amber px-3 py-1.5 text-text-primary font-mono text-sm"
            />
            {(statusFilter || dateFilter || searchFilter) && (
              <button
                onClick={() => { setStatusFilter(''); setDateFilter(''); setSearchFilter(''); setAllDispatchesPage(1); }}
                className="text-text-tertiary hover:text-amber text-xs font-mono uppercase"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {isLoadingAll ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-3 h-3 bg-amber animate-pulse" />
            <span className="text-text-secondary font-mono ml-3">Loading dispatches...</span>
          </div>
        ) : allDispatches.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-text-tertiary font-mono">No dispatches found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-text-tertiary text-xs uppercase font-mono border-b border-panel-border">
                    <th className="px-4 py-3">Dispatch #</th>
                    <th className="px-4 py-3">Order #</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Scheduled Date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Truck</th>
                    <th className="px-4 py-3">Driver</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allDispatches.map((d) => {
                    const config = STATUS_CONFIG[d.status] || STATUS_CONFIG.SCHEDULED;
                    return (
                      <tr key={d.id} className="border-b border-panel-border/50 hover:bg-panel-border/20 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/dispatch/${d.id}`} className="text-amber font-mono text-sm hover:underline">
                            {d.dispatchNumber}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-text-secondary font-mono text-sm">
                          {d.order?.orderNumber || '—'}
                        </td>
                        <td className="px-4 py-3 text-text-primary text-sm">
                          {d.order?.customer?.name || '—'}
                        </td>
                        <td className="px-4 py-3 text-text-secondary font-mono text-sm">
                          {new Date(d.scheduledDate).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-mono border ${config!.bgColor} ${config!.color}`}>
                            {config!.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-text-secondary font-mono text-sm">
                          {d.truck?.unitNumber || 'Unassigned'}
                        </td>
                        <td className="px-4 py-3 text-text-secondary text-sm">
                          {d.driver ? `${d.driver.firstName} ${d.driver.lastName}` : 'Unassigned'}
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/dispatch/${d.id}`}
                            className="flex items-center space-x-1 text-steel hover:text-amber transition-colors text-xs font-mono uppercase"
                          >
                            <Eye className="w-3 h-3" />
                            <span>View</span>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {allDispatchesTotal > 25 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-panel-border">
                <span className="text-text-tertiary text-xs font-mono">
                  Page {allDispatchesPage} of {Math.ceil(allDispatchesTotal / 25)}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setAllDispatchesPage(p => Math.max(1, p - 1))}
                    disabled={allDispatchesPage === 1}
                    className="px-3 py-1 text-xs font-mono bg-background border border-panel-border hover:border-steel text-text-secondary disabled:opacity-50 transition-all"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setAllDispatchesPage(p => p + 1)}
                    disabled={allDispatchesPage >= Math.ceil(allDispatchesTotal / 25)}
                    className="px-3 py-1 text-xs font-mono bg-background border border-panel-border hover:border-steel text-text-secondary disabled:opacity-50 transition-all"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function StatCard({
  title,
  value,
  color,
  icon: Icon,
}: {
  title: string;
  value: number;
  color: 'steel' | 'amber' | 'success-green' | 'error-red';
  icon: React.ElementType;
}) {
  const colorClasses = {
    steel: 'text-steel border-steel/30 shadow-[0_0_10px_rgba(92,140,199,0.2)]',
    amber: 'text-amber border-amber/30 shadow-[0_0_10px_rgba(255,149,0,0.2)]',
    'success-green':
      'text-success-green border-success-green/30 shadow-[0_0_10px_rgba(34,197,94,0.2)]',
    'error-red': 'text-error-red border-error-red/30 shadow-[0_0_10px_rgba(239,68,68,0.2)]',
  };

  const classes = colorClasses[color];

  return (
    <div
      className={`relative stat-card bg-panel-dark border ${classes} p-4 clip-path-octagon`}
    >
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-current opacity-50" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-current opacity-50" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-current opacity-50" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-current opacity-50" />

      <div className="flex items-center justify-between">
        <div>
          <p className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-1">
            {title}
          </p>
          <p className={`text-3xl font-display font-bold ${classes.split(' ')[0]}`}>
            {value}
          </p>
        </div>
        <Icon className={`w-10 h-10 ${classes.split(' ')[0]} opacity-50`} />
      </div>
    </div>
  );
}

// Droppable Column Component
function DroppableColumn({
  status,
  config,
  dispatches,
  isUpdating,
}: {
  status: string;
  config: { label: string; color: string; bgColor: string; icon: React.ElementType };
  dispatches: DispatchItem[];
  isUpdating: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  const Icon = config.icon;

  return (
    <div
      ref={setNodeRef}
      className={`bg-panel-dark border min-h-[500px] transition-all ${
        isOver ? 'border-amber shadow-[0_0_15px_rgba(255,149,0,0.3)]' : 'border-panel-border'
      }`}
    >
      {/* Column Header */}
      <div
        className={`flex items-center justify-between p-3 border-b border-panel-border ${config.bgColor}`}
      >
        <div className="flex items-center space-x-2">
          <Icon className={`w-5 h-5 ${config.color}`} />
          <span className={`font-display uppercase tracking-wide text-sm ${config.color}`}>
            {config.label}
          </span>
        </div>
        <span
          className={`px-2 py-0.5 text-xs font-mono ${config.color} bg-background/50 border ${config.bgColor}`}
        >
          {dispatches.length}
        </span>
      </div>

      {/* Dispatch Cards */}
      <div className="p-2 space-y-2 max-h-[600px] overflow-y-auto">
        {isUpdating && isOver && (
          <div className="text-center py-2">
            <div className="w-4 h-4 border-2 border-amber border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        )}
        {dispatches.length === 0 ? (
          <div className={`text-center py-8 border-2 border-dashed transition-all ${
            isOver ? 'border-amber/50 bg-amber/5' : 'border-transparent'
          }`}>
            <p className="text-text-tertiary text-xs font-mono">
              {isOver ? 'Drop here' : 'No dispatches'}
            </p>
          </div>
        ) : (
          dispatches.map((dispatch) => (
            <DraggableDispatchCard key={dispatch.id} dispatch={dispatch} />
          ))
        )}
      </div>
    </div>
  );
}

// Draggable Dispatch Card Wrapper
function DraggableDispatchCard({ dispatch }: { dispatch: DispatchItem }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dispatch.id,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${isDragging ? 'opacity-50' : ''}`}
    >
      <DispatchCard
        dispatch={dispatch}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

// Dispatch Card Component
function DispatchCard({
  dispatch,
  compact = false,
  isDragging = false,
  dragHandleProps,
}: {
  dispatch: DispatchItem;
  compact?: boolean;
  isDragging?: boolean;
  dragHandleProps?: Record<string, unknown>;
}) {
  const totalQuantity = dispatch.order.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div
      className={`block bg-background border transition-all ${
        isDragging
          ? 'border-amber shadow-[0_0_20px_rgba(255,149,0,0.4)]'
          : 'border-panel-border hover:border-amber/50'
      } ${compact ? 'p-2' : 'p-3'}`}
    >
      {/* Header with drag handle */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {dragHandleProps && (
            <button
              {...dragHandleProps}
              className="cursor-grab active:cursor-grabbing text-text-tertiary hover:text-amber touch-none"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
          <Link
            to={`/dispatch/${dispatch.id}`}
            className="text-amber font-mono text-xs hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {dispatch.dispatchNumber}
          </Link>
        </div>
        {dispatch.scheduledTime && (
          <span className="text-text-tertiary text-xs font-mono">{dispatch.scheduledTime}</span>
        )}
      </div>

      {/* Customer */}
      <div className="mb-2">
        <p className="text-text-primary text-sm font-medium truncate">
          {dispatch.order.customer.name}
        </p>
        <p className="text-text-tertiary text-xs font-mono">
          {dispatch.order.orderNumber}
        </p>
      </div>

      {!compact && (
        <>
          {/* Address */}
          {dispatch.order.deliveryAddress && (
            <div className="flex items-start space-x-1 mb-2">
              <MapPin className="w-3 h-3 text-steel mt-0.5 flex-shrink-0" />
              <p className="text-text-secondary text-xs truncate">
                {dispatch.order.deliveryCity}, {dispatch.order.deliveryState}
              </p>
            </div>
          )}

          {/* Product Summary */}
          <div className="flex items-center space-x-1 mb-2">
            <Package className="w-3 h-3 text-steel" />
            <p className="text-text-secondary text-xs">
              {totalQuantity.toLocaleString()} gal
            </p>
          </div>

          {/* Truck & Driver */}
          <div className="flex items-center justify-between text-xs border-t border-panel-border pt-2 mt-2">
            <div className="flex items-center space-x-1">
              <Truck className="w-3 h-3 text-amber" />
              <span className="text-text-tertiary font-mono">{dispatch.truck.unitNumber}</span>
            </div>
            <div className="flex items-center space-x-1">
              <User className="w-3 h-3 text-steel" />
              <span className="text-text-tertiary font-mono">
                {dispatch.driver.firstName.charAt(0)}. {dispatch.driver.lastName}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DispatchPage;
